import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cbt-modal';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { APIErrorNotification } from 'common/components';
import { usePluralization } from 'common/hooks';
import { Typography } from 'common/typography';
import { localize } from 'common/utils';
import { useShareMenuWithVenuesTracking } from 'modules/menu/hooks/telemetryHooks';
import { FullSelection } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';
import { SharedMenu } from 'modules/shared-menu/types';

import { linkVenuesToSharedMenu } from '../../../api';
import { VenueWithSharedMenuLinkInfo } from '../../../hooks/useVenuesWithSelectionLinks';

type Props = {
  selectedVenues: VenueWithSharedMenuLinkInfo[];
  selectedMenu: SharedMenu;
  selectionData: FullSelection;
  onBack: () => void;
  onClose: () => void;
  refetchSharedMenus: () => void;
  allVenuesSelected?: boolean;
  search?: string;
};

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
  gap: 16px;
`;

const StyledModalFooter = styled(ModalFooter)`
  display: flex;
  flex-direction: column;
`;

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`;

export const ConfirmAndShare = ({
  selectedVenues,
  selectedMenu,
  selectionData,
  allVenuesSelected,
  search,
  refetchSharedMenus,
  onBack,
  onClose,
}: Props) => {
  const { locale, formatMessage } = useIntl();
  const { FormattedPluralMessage } = usePluralization();

  const selectedVenuesLinkedToSharedMenu = useMemo(
    () => selectedVenues.filter(venue => venue.sharedMenuLink.scope !== 'none'),
    [selectedVenues],
  );

  const isMenuEmpty = useMemo(() => !selectionData?.offers?.length, [selectionData]);
  const selectedVenueIds = useMemo(() => selectedVenues.map(venue => venue.id), [selectedVenues]);

  const { mutate, isLoading, error, reset } = useMutation<unknown, Error>(
    () => {
      if (!selectedMenu) {
        return Promise.reject();
      }
      return linkVenuesToSharedMenu(selectedMenu.id, selectedVenueIds);
    },
    {
      onSuccess: async () => {
        await refetchSharedMenus();
        toast.success(<FormattedMessage id="shared-menu.link.success" />);
        handleClose();
      },
    },
  );

  const trackShareWithVenues = useShareMenuWithVenuesTracking(
    allVenuesSelected ?? false,
    selectedMenu.id ?? '',
    search ?? '',
    selectedVenueIds,
  );

  const handleSubmit = useCallback(() => {
    trackShareWithVenues();
    mutate();
  }, [trackShareWithVenues, mutate]);

  const handleBack = useCallback(() => {
    reset();
    onBack();
  }, [reset, onBack]);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  return (
    <>
      <ModalHeader onCloseClick={handleClose}>
        <FormattedMessage id="listing-manager.unassigned-venues.share.confirm-menu" />
      </ModalHeader>
      <StyledModalContent>
        <TextContainer>
          {isMenuEmpty && (
            <Alert
              title={formatMessage({ id: 'shared-menu.link.venues-selected.menu-empty-note-label' })}
              size="small"
              variant="warning"
            >
              <FormattedMessage id="shared-menu.link.venues-selected.menu-empty-note" />
            </Alert>
          )}
          {!!selectedVenuesLinkedToSharedMenu.length && (
            <Alert
              size="small"
              variant="info"
              title="" // title cannot normally not be present when showMoreContent is defined
              showMoreContent={
                <ul>
                  {selectedVenuesLinkedToSharedMenu.map(venue => (
                    <li key={venue.id}>{localize(venue.name, locale)}</li>
                  ))}
                </ul>
              }
            >
              <FormattedPluralMessage
                id="shared-menu.link.shared-menu-selected.warnings.existing-links.description"
                count={selectedVenuesLinkedToSharedMenu.length}
              />
            </Alert>
          )}
          <Alert variant="info" size="small">
            <FormattedMessage id="shared-menu.link.venues-selected.modal-wolt-specials-note" />
          </Alert>
        </TextContainer>
        <TextContainer>
          <Typography.Body3>
            <FormattedMessage
              id="shared-menu.link.venues-selected.share-confirm"
              values={{
                strong: chunks => <strong>{chunks}</strong>,
                count: selectedVenues.length,
                name: localize(selectedMenu.name, locale),
              }}
            />
          </Typography.Body3>
        </TextContainer>
      </StyledModalContent>
      <StyledModalFooter>
        {error && <APIErrorNotification error={error} style={{ marginBlockEnd: '16px', width: '100%' }} />}
        <ModalButtons>
          <Button variant="secondary" size="small" style={{ marginInlineEnd: '8px' }} onClick={handleBack}>
            <FormattedMessage id="state.back" />
          </Button>
          <Button
            variant="primary"
            size="small"
            disabled={selectedVenues.length === 0 || !selectedMenu || isLoading}
            loading={isLoading}
            onClick={handleSubmit}
          >
            {isMenuEmpty ? (
              <FormattedMessage id="shared-menu.link.venues-selected.share-empty-menu" />
            ) : (
              <FormattedMessage id="shared-menu.link.venues-selected.share-menu" />
            )}
          </Button>
        </ModalButtons>
      </StyledModalFooter>
    </>
  );
};
